import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, Heart, DollarSign, Newspaper } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { ResourceAdmin } from '../admin/ResourceAdmin';
import { RecordsTable, formatDate } from '../admin/RecordsTable';
import { GalleryAdmin } from '../admin/GalleryAdmin';
import { InterestAdmin } from '../admin/InterestAdmin';
import { ContentAdmin } from '../admin/ContentAdmin';
import { CommitteeRoster } from '../admin/CommitteeRoster';
import { NewsletterBlast } from '../admin/NewsletterBlast';
import { MinutesAdmin } from '../admin/MinutesAdmin';
import { ProfilesAdmin } from '../admin/ProfilesAdmin';
import { PastMeetingsAdmin } from '../admin/PastMeetingsAdmin';
import { MemberManager } from '../admin/MemberManager';
import { AnalyticsAdmin } from '../admin/AnalyticsAdmin';
import { CommunityEventsAdmin } from '../admin/CommunityEventsAdmin';
import { ParadeRosterAdmin } from '../admin/ParadeRosterAdmin';
import { ExportCsvButton } from '../admin/ExportCsvButton';
import api from '../../lib/api';

const eventFields = [
  { name: 'title', label: 'Title', type: 'text', required: true },
  { name: 'date', label: 'Date', type: 'text', required: true, placeholder: '2026-09-15', hint: 'Format: YYYY-MM-DD' },
  { name: 'time', label: 'Time', type: 'text', required: true, placeholder: '6:00 PM - 8:00 PM' },
  { name: 'location', label: 'Location', type: 'text', required: true },
  { name: 'description', label: 'Description', type: 'textarea', required: true },
  { name: 'category', label: 'Category', type: 'text', required: true, placeholder: 'Meeting, Workshop, Volunteer, Fundraiser, or Celebration' },
  { name: 'image', label: 'Image URL', type: 'text' },
  { name: 'spots_available', label: 'Spots Available', type: 'number', required: true },
];

const programFields = [
  { name: 'title', label: 'Title', type: 'text', required: true },
  { name: 'description', label: 'Description', type: 'textarea', required: true },
  { name: 'icon', label: 'Icon', type: 'text', placeholder: 'Palette, Megaphone, BookOpen, Leaf, or HeartPulse' },
  { name: 'image', label: 'Image URL', type: 'text' },
  { name: 'goals', label: 'Goals', type: 'list', hint: 'One goal per line' },
  { name: 'order', label: 'Display Order', type: 'number' },
];

const newsFields = [
  { name: 'title', label: 'Title', type: 'text', required: true },
  { name: 'excerpt', label: 'Excerpt / Summary', type: 'textarea', required: true },
  { name: 'content', label: 'Full Article (optional)', type: 'textarea', hint: 'Full article text — one paragraph per line. Shown on the News page featured post.' },
  { name: 'author', label: 'Author', type: 'text', required: true },
  { name: 'category', label: 'Category', type: 'text', placeholder: 'Announcements, Events, Community…' },
  { name: 'image', label: 'Image URL', type: 'text' },
  { name: 'date', label: 'Date', type: 'text', placeholder: '2026-08-01', hint: 'Format: YYYY-MM-DD (defaults to today)' },
];

const boardFields = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'title', label: 'Board Title', type: 'text', required: true },
  { name: 'bio', label: 'Bio', type: 'textarea', required: true },
  { name: 'image', label: 'Photo URL', type: 'text' },
  { name: 'order', label: 'Display Order', type: 'number' },
];

const memberColumns = [
  { key: 'created_at', label: 'Joined', render: (r) => formatDate(r.created_at) },
  { key: 'name', label: 'Name', render: (r) => `${r.first_name} ${r.last_name}`.trim() },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone', render: (r) => r.phone || '—' },
  {
    key: 'membership_level',
    label: 'Level',
    render: (r) => (
      <Badge className={r.role === 'admin' ? 'bg-[#bd8cae]/30 text-[#a3688f]' : 'bg-[#5d6b3c]/10 text-[#5d6b3c]'}>
        {r.role === 'admin' ? 'Admin' : r.membership_level || 'Member'}
      </Badge>
    ),
  },
  { key: 'member_id', label: 'Member ID' },
];

const messageColumns = [
  { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'subject', label: 'Subject' },
  { key: 'message', label: 'Message', render: (r) => <span className="block max-w-md">{r.message}</span> },
];

const subscriberColumns = [
  { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
  { key: 'email', label: 'Email' },
];

const rsvpColumns = [
  { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
  { key: 'event_title', label: 'Event' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'guests', label: 'Guests' },
  { key: 'dish', label: 'Dish', render: (r) => r.dish || '—' },
  { key: 'dietary_notes', label: 'Dietary Notes', render: (r) => r.dietary_notes || '—' },
];

const volunteerSignupColumns = [
  { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'opportunity', label: 'Opportunity' },
  { key: 'message', label: 'Note', render: (r) => <span className="block max-w-xs">{r.message || '—'}</span> },
];

const volunteerHoursColumns = [
  { key: 'date', label: 'Date' },
  { key: 'name', label: 'Member' },
  { key: 'activity', label: 'Activity' },
  { key: 'hours', label: 'Hours' },
];

const donationColumns = [
  { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
  {
    key: 'type',
    label: 'Type',
    render: (r) => (
      <Badge className="bg-[#5d6b3c]/10 text-[#5d6b3c] capitalize">
        {(r.type || 'donation').replace('_', ' ')}
      </Badge>
    ),
  },
  { key: 'donor_name', label: 'Donor', render: (r) => (r.anonymous ? 'Anonymous' : r.donor_name || '—') },
  { key: 'amount', label: 'Amount', render: (r) => `$${((r.amount || 0) / 100).toLocaleString()}` },
  {
    key: 'payment_status',
    label: 'Status',
    render: (r) => (
      <Badge className={r.payment_status === 'paid' ? 'bg-[#9dad84]/40 text-[#5d6b3c]' : 'bg-[#9dad84]/30 text-[#5f6d51]'}>
        {r.payment_status}
      </Badge>
    ),
  },
];

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="bg-white border-0 shadow-md">
    <CardContent className="p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-[#5d6b3c]/15 flex items-center justify-center mx-auto mb-3">
        <Icon className="w-6 h-6 text-[#5d6b3c]" />
      </div>
      <p className="font-serif text-3xl text-[#3c4a2a]" data-testid={testId}>{value}</p>
      <p className="text-sm text-[#3c4a2a]/60 mt-1">{label}</p>
    </CardContent>
  </Card>
);

const AdminPage = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/admin/stats').then((res) => setStats(res.data)).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen pt-32 bg-[#f1e3eb]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
            Board Tools
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-2">
            Admin Panel
          </h1>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <StatCard icon={Users} label="Members" value={stats.members} testId="admin-stat-members" />
            <StatCard icon={Calendar} label="Event RSVPs" value={stats.rsvps} testId="admin-stat-rsvps" />
            <StatCard icon={Mail} label="Messages" value={stats.contact_submissions} testId="admin-stat-messages" />
            <StatCard icon={Newspaper} label="Subscribers" value={stats.newsletter_subscribers} testId="admin-stat-subscribers" />
            <StatCard
              icon={DollarSign}
              label="Total Raised"
              value={`$${((stats.total_raised_cents || 0) / 100).toLocaleString()}`}
              testId="admin-stat-raised"
            />
          </div>
        )}

        <Tabs defaultValue="events" className="bg-white rounded-xl shadow-md p-6">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-[#f1e3eb] mb-6">
            <TabsTrigger value="content" data-testid="admin-tab-content">Page Content</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>
            <TabsTrigger value="programs" data-testid="admin-tab-programs">Programs</TabsTrigger>
            <TabsTrigger value="board" data-testid="admin-tab-board">Board</TabsTrigger>
            <TabsTrigger value="news" data-testid="admin-tab-news">News</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="rsvps" data-testid="admin-tab-rsvps">RSVPs</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="admin-tab-volunteers">Volunteers</TabsTrigger>
            <TabsTrigger value="donations" data-testid="admin-tab-donations">Donations</TabsTrigger>
            <TabsTrigger value="past-meetings" data-testid="admin-tab-past-meetings">Past Meetings</TabsTrigger>
            <TabsTrigger value="responses" data-testid="admin-tab-responses">Form Responses</TabsTrigger>
            <TabsTrigger value="minutes" data-testid="admin-tab-minutes">Minutes</TabsTrigger>
            <TabsTrigger value="traffic" data-testid="admin-tab-traffic">Site Traffic</TabsTrigger>
          </TabsList>

          <TabsContent value="past-meetings">
            <PastMeetingsAdmin />
          </TabsContent>

          <TabsContent value="responses">
            <ProfilesAdmin />
          </TabsContent>

          <TabsContent value="minutes">
            <MinutesAdmin />
          </TabsContent>

          <TabsContent value="traffic">
            <AnalyticsAdmin />
          </TabsContent>

          <TabsContent value="content">
            <ContentAdmin />
          </TabsContent>

          <TabsContent value="events">
            <ParadeRosterAdmin />
            <CommunityEventsAdmin />
            <ResourceAdmin
              resource="events"
              listEndpoint="/events"
              title="Manage Events"
              itemLabel="Event"
              fields={eventFields}
              renderSummary={(item) => (
                <div>
                  <p className="font-medium text-[#3c4a2a] truncate">{item.title}</p>
                  <p className="text-sm text-[#5f6d51]/70">
                    {item.date} • {item.location} • {item.spots_available} spots left
                  </p>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="programs">
            <ResourceAdmin
              resource="programs"
              listEndpoint="/programs"
              title="Manage Programs"
              itemLabel="Program"
              fields={programFields}
              renderSummary={(item) => (
                <div>
                  <p className="font-medium text-[#3c4a2a] truncate">{item.title}</p>
                  <p className="text-sm text-[#5f6d51]/70 line-clamp-1">{item.description}</p>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="board">
            <ResourceAdmin
              resource="board-members"
              listEndpoint="/board-members"
              title="Manage Board Members"
              itemLabel="Board Member"
              fields={boardFields}
              renderSummary={(item) => (
                <div>
                  <p className="font-medium text-[#3c4a2a] truncate">{item.name}</p>
                  <p className="text-sm text-[#5f6d51]/70">{item.title}</p>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="news">
            <ResourceAdmin
              resource="news"
              listEndpoint="/news"
              title="Manage News Posts"
              itemLabel="News Post"
              fields={newsFields}
              renderSummary={(item) => (
                <div>
                  <p className="font-medium text-[#3c4a2a] truncate">{item.title}</p>
                  <p className="text-sm text-[#5f6d51]/70">
                    {item.date} • {item.author} • {item.category}
                  </p>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="gallery">
            <GalleryAdmin />
          </TabsContent>

          <TabsContent value="members">
            <MemberManager />
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-[#3c4a2a]">Registered Members</h2>
              <ExportCsvButton
                endpoint="/admin/members"
                filename="wclc-members.csv"
                testId="export-members-btn"
                columns={[
                  { label: 'Joined', value: (r) => formatDate(r.created_at) },
                  { label: 'First Name', key: 'first_name' },
                  { label: 'Last Name', key: 'last_name' },
                  { label: 'Email', key: 'email' },
                  { label: 'Phone', key: 'phone' },
                  { label: 'Level', value: (r) => (r.role === 'admin' ? 'Admin' : r.membership_level || 'Member') },
                  { label: 'Member ID', key: 'member_id' },
                ]}
              />
            </div>
            <RecordsTable
              endpoint="/admin/members"
              testId="admin-members-table"
              emptyText="No members yet."
              columns={memberColumns}
            />
          </TabsContent>

          <TabsContent value="messages">
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6">Contact Messages</h2>
            <RecordsTable
              endpoint="/admin/contact-submissions"
              testId="admin-messages-table"
              emptyText="No contact messages yet."
              columns={messageColumns}
            />
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6 mt-10">Founding Member Interest</h2>
            <InterestAdmin />
          </TabsContent>

          <TabsContent value="subscribers">
            <NewsletterBlast />
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-[#3c4a2a]">Newsletter Subscribers</h2>
              <ExportCsvButton
                endpoint="/admin/newsletter-subscribers"
                filename="wclc-subscribers.csv"
                testId="export-subscribers-btn"
                columns={[
                  { label: 'Date', value: (r) => formatDate(r.created_at) },
                  { label: 'Email', key: 'email' },
                ]}
              />
            </div>
            <RecordsTable
              endpoint="/admin/newsletter-subscribers"
              testId="admin-subscribers-table"
              emptyText="No subscribers yet."
              columns={[
                { key: 'created_at', label: 'Date', render: (r) => formatDate(r.created_at) },
                { key: 'email', label: 'Email' },
              ]}
            />
          </TabsContent>

          <TabsContent value="rsvps">
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6">Event RSVPs</h2>
            <RecordsTable
              endpoint="/admin/rsvps"
              testId="admin-rsvps-table"
              emptyText="No RSVPs yet."
              columns={rsvpColumns}
            />
          </TabsContent>

          <TabsContent value="volunteers">
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6">Committee Roster</h2>
            <CommitteeRoster />
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6 mt-10">Volunteer Signups</h2>
            <RecordsTable
              endpoint="/admin/volunteer-signups"
              testId="admin-volunteer-signups-table"
              emptyText="No volunteer signups yet."
              columns={volunteerSignupColumns}
            />
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6 mt-10">Logged Volunteer Hours</h2>
            <RecordsTable
              endpoint="/admin/volunteer-hours"
              testId="admin-volunteer-hours-table"
              emptyText="No volunteer hours logged yet."
              columns={volunteerHoursColumns}
            />
          </TabsContent>

          <TabsContent value="donations">
            <h2 className="font-serif text-xl text-[#3c4a2a] mb-6">Donations & Payments</h2>
            <RecordsTable
              endpoint="/admin/donations"
              testId="admin-donations-table"
              emptyText="No payments yet."
              columns={donationColumns}
            />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default AdminPage;
